package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.PageGutter
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.api.DestinationState
import uz.mint.frontend.api.pathIn
import uz.mint.frontend.api.rememberDestinations
import uz.mint.frontend.components.widgets.ArrowRightIcon
import uz.mint.frontend.components.widgets.PhoneIcon
import uz.mint.frontend.components.widgets.SearchIcon
import uz.mint.frontend.components.widgets.TelegramIcon
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.placeItems
import uz.mint.frontend.widthCss
import com.varabyte.kobweb.compose.ui.graphics.Colors
import uz.mint.frontend.api.flag
import uz.mint.frontend.api.priceLabel

@Composable
fun Hero(locale: Locale, strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            .id(TOP_ANCHOR)
            .borderBottom(2.px, LineStyle.Solid, palette.rule)
            .toAttrs()
    ) {
        Div(
            Modifier
                .fillMaxWidth().maxWidth(1360.px).marginInlineAuto()
                .display(DisplayStyle.Grid)
                .gridTemplateColumnsCss("repeat(auto-fit,minmax(min(100%,440px),1fr))")
                .toAttrs()
        ) {
            HeroCopy(strings, palette.accentText, palette.ink2, palette.mint, palette.onMint,
                palette.solidBg, palette.solidFg, palette.rule)
            HeroPoster(locale, strings)
        }
    }
}

@Composable
private fun HeroCopy(
    strings: SiteStrings,
    accent: com.varabyte.kobweb.compose.ui.graphics.Color,
    ink2: com.varabyte.kobweb.compose.ui.graphics.Color,
    mint: com.varabyte.kobweb.compose.ui.graphics.Color,
    onMint: com.varabyte.kobweb.compose.ui.graphics.Color,
    solidBg: com.varabyte.kobweb.compose.ui.graphics.Color,
    solidFg: com.varabyte.kobweb.compose.ui.graphics.Color,
    rule: com.varabyte.kobweb.compose.ui.graphics.Color,
) {
    Column(
        Modifier
            .padding(
                top = clamp(36.px, 4.4.vw, 72.px),
                bottom = clamp(32.px, 3.4.vw, 56.px),
                leftRight = PageGutter,
            )
            .gap(26.px)
            .justifyContent("center")
    ) {
        SpanText(strings.heroEyebrow, EyebrowStyle.toModifier().color(accent))

        H1(
            Modifier
                .fontSize(clamp(40.px, 5.vw, 76.px))
                .lineHeight(1.02)
                .letterSpacing((-0.035).em)
                .fontWeight(FontWeight.ExtraBold)
                .toAttrs()
        ) {
            SpanText(strings.heroTitle1)
            Br()
            // Highlighted phrase: a mint marker behind the text, bleeding slightly past it.
            //
            // `inline-block` with an explicit line-height, rather than a plain inline span, and
            // that is the whole fix for the block standing proud of the letters. The background
            // of an *inline* box is the font's content area — ascent plus descent, around 1.3em
            // in Manrope — while the capitals it looks like it should hug are about 0.72em. The
            // slack shows up above the text, because that is where a font keeps room for accents
            // nothing here uses. line-height has no effect on an inline background; on an
            // inline-block it sets the box outright, so the padding below is what the marker
            // actually extends by, and the extra at the bottom is for the descenders in
            // "роуминга".
            SpanText(
                strings.heroTitle2,
                Modifier
                    .backgroundColor(mint).color(onMint)
                    .padding(top = 0.04.em, bottom = 0.13.em, leftRight = 0.12.em)
                    .margin(left = (-0.12).em)
                    .styleModifier {
                        property("display", "inline-block")
                        property("line-height", "0.9")
                        property("box-decoration-break", "clone")
                    },
            )
        }

        P(
            Modifier
                .fontSize(clamp(16.px, 1.25.vw, 19.px))
                .lineHeight(1.5).maxWidth(34.em).color(ink2)
                .toAttrs()
        ) { SpanText(strings.heroLede) }

        Row(Modifier.gap(12.px).flexWrapCss().margin(top = 2.px)) {
            Link(
                path = "#$TARIFFS_ANCHOR",
                modifier = Modifier
                    .backgroundColor(solidBg).color(solidFg)
                    .fontWeight(FontWeight.Bold).fontSize(16.px)
                    .padding(topBottom = 17.px, leftRight = 28.px)
                    .whiteSpace(WhiteSpace.NoWrap),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) { SpanText(strings.heroCta1) }

            // Was "Проверить телефон" pointing at #advantages, which is a section about why
            // people buy here — no compatibility check anywhere on it. A button that names one
            // thing and does another costs more trust than it saves, so it now does the one
            // thing a visitor with a question actually wants.
            Link(
                path = SiteConfig.TELEGRAM_URL,
                modifier = Modifier
                    .border(2.px, LineStyle.Solid, rule)
                    .fontWeight(FontWeight.Bold).fontSize(16.px)
                    .padding(topBottom = 15.px, leftRight = 26.px)
                    .whiteSpace(WhiteSpace.NoWrap),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) {
                Row(Modifier.gap(9.px), verticalAlignment = Alignment.CenterVertically) {
                    TelegramIcon(size = 17, color = rule)
                    SpanText(strings.heroCta2)
                }
            }

            // The phone beside Telegram, not instead of it: they answer different people. Telegram
            // is where a question gets a written answer and a link back; a call is what someone
            // reaches for when they are about to travel and want a person now. Labelled with the
            // number itself rather than a word, because the number is the thing being offered.
            Link(
                path = SiteConfig.PHONE_HREF,
                modifier = Modifier
                    .border(2.px, LineStyle.Solid, rule)
                    .fontWeight(FontWeight.Bold).fontSize(16.px)
                    .padding(topBottom = 15.px, leftRight = 26.px)
                    .whiteSpace(WhiteSpace.NoWrap),
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            ) {
                Row(Modifier.gap(9.px), verticalAlignment = Alignment.CenterVertically) {
                    PhoneIcon(size = 17, color = rule)
                    SpanText(SiteConfig.PHONE)
                }
            }
        }
    }
}

/** Mint panel with the coverage poster and the destination finder card. */
@Composable
private fun HeroPoster(locale: Locale, strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()

    // A column, not an overlay. The counter used to be positioned absolutely at the top while the
    // finder card was pinned to the bottom of the same box — which reads correctly only while the
    // panel is taller than the card. On a phone the card is most of a screen tall, so it grew
    // upwards and sat straight on top of "180+ СТРАН", with the digits showing through it.
    //
    // Stacking them and pushing them apart with space-between keeps the desktop composition — the
    // counter at the top of the panel, the card at its foot — and cannot collide at any width,
    // because nothing is out of flow any more.
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(palette.mint).color(palette.onMint)
            .borderLeft(2.px, LineStyle.Solid, palette.rule)
            .minHeight(clamp(360.px, 36.vw, 540.px))
            .justifyContent("space-between")
            .gap(clamp(24.px, 3.vw, 40.px))
            .padding(topBottom = clamp(24.px, 3.vw, 44.px), leftRight = PageGutter)
    ) {
        Row(
            Modifier.fillMaxWidth().gap(16.px),
            verticalAlignment = Alignment.Top,
        ) {
            Column {
                Div(
                    Modifier.fontSize(clamp(48.px, 5.6.vw, 84.px)).lineHeight(0.86)
                        .fontWeight(FontWeight.ExtraBold).letterSpacing((-0.05).em).toAttrs()
                ) { SpanText("180+") }
                Div(
                    EyebrowStyle.toModifier().fontSize(13.px).letterSpacing(0.1.em).margin(top = 10.px).toAttrs()
                ) { SpanText(strings.heroPoster) }
            }
            com.varabyte.kobweb.compose.foundation.layout.Spacer()
            Img(
                src = "/mint-mark-ink.svg",
                alt = "",
                attrs = Modifier.height(clamp(30.px, 3.vw, 44.px)).opacity(0.9).toAttrs(),
            )
        }

        DestinationFinder(locale, strings)
    }
}

/**
 * "Куда вы едете?" — the question it asks is *where*, and the answer is a place, not a package.
 *
 * It used to list plans: typing "Турция" returned four Turkish packages with prices, and picking
 * one opened checkout. That inverted the flow the rest of the storefront was rebuilt around — the
 * customer chose a product before ever seeing what else that country sells, out of the two to
 * forty-three packages it has. Now a row is a country and picking it opens that country's
 * packages, which is the same journey the destination grid below takes.
 *
 * The search is **client-side** here, unlike the plan search it replaces. The whole index is ~225
 * names in one small response, so filtering it costs no request per keystroke — and there is
 * nothing to debounce. It is the same rule the picker on `/plans` follows, and the inverse of the
 * plan rule, where filtering in the browser would mean downloading 2 900 rows first.
 *
 * Rows are real links, so each is a crawlable route into a filtered catalogue and any of them
 * opens in a new tab.
 */
@Composable
private fun DestinationFinder(locale: Locale, strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()
    var query by remember { mutableStateOf("") }

    val (state, _) = rememberDestinations(locale)
    val destinations = (state as? DestinationState.Loaded)?.destinations

    // Empty box: the curated shortlist, so the card is useful before a single keystroke. Typing:
    // the real answer across all 191 countries and 35 bundles, countries first — a bundle is the
    // rarer intent and would otherwise push the country someone typed off the visible rows.
    val matches = when {
        destinations == null -> emptyList()
        query.isBlank() -> destinations.popular
        else -> destinations.search(query).let { it.countries + it.regions }
    }.take(FINDER_RESULTS)

    Column(
        Modifier
            // Straight from the design: never wider than its column, but shrinks with the viewport.
            .widthCss("min(100%, clamp(300px, 29vw, 392px))")
            .styleModifier { property("margin-left", "auto") }
            .backgroundColor(palette.card).color(palette.ink)
            .border(2.px, LineStyle.Solid, palette.rule)
    ) {
        Column(
            Modifier.fillMaxWidth().padding(top = 16.px, bottom = 14.px, leftRight = 20.px).gap(5.px)
                .borderBottom(2.px, LineStyle.Solid, palette.rule)
        ) {
            Div(Modifier.fontSize(17.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.02).em).toAttrs()) {
                SpanText(strings.finderTitle)
            }
            SpanText(strings.finderSub, Modifier.fontSize(13.px).color(palette.ink2))
        }

        Row(
            Modifier.fillMaxWidth().gap(10.px).padding(topBottom = 11.px, leftRight = 20.px)
                .borderBottom(2.px, LineStyle.Solid, palette.rule),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SearchIcon(color = palette.ink2)
            Input(
                type = InputType.Text,
                attrs = Modifier
                    .flex(1).minWidth(0.px)
                    .border(0.px, LineStyle.None, palette.rule)
                    .backgroundColor(Colors.Transparent)
                    .color(palette.ink)
                    .fontSize(15.px).fontWeight(FontWeight.SemiBold)
                    .outline(0.px, LineStyle.None, palette.rule)
                    .toAttrs {
                        value(query)
                        attr("placeholder", strings.finderPlaceholder)
                        onInput { query = it.value }
                    }
            )
        }

        if (matches.isEmpty()) {
            Div(
                Modifier.padding(topBottom = 14.px, leftRight = 20.px).fontSize(13.px)
                    .lineHeight(1.45).color(palette.ink2)
                    .borderBottom(2.px, LineStyle.Solid, palette.rule).toAttrs()
            ) {
                // "Nothing found" is only true once the index has actually arrived; before that it
                // would be a lie, and this card is one of the things a static export bakes in.
                SpanText(if (state is DestinationState.Loaded) strings.finderNone else strings.tariffsLoading)
            }
        } else {
            matches.forEach { destination ->
                Link(
                    path = destination.pathIn(locale),
                    modifier = Modifier
                        .fillMaxWidth()
                        .display(DisplayStyle.Flex).alignItems("center").gap(12.px)
                        .padding(topBottom = 11.px, leftRight = 20.px)
                        .borderBottom(2.px, LineStyle.Solid, palette.rule),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                ) {
                    SpanText(destination.flag(), Modifier.fontSize(19.px).lineHeight(1))
                    SpanText(
                        destination.name,
                        Modifier.flex(1).minWidth(0.px).fontSize(15.px)
                            .fontWeight(FontWeight.Bold).letterSpacing((-0.01).em),
                    )
                    SpanText(
                        destination.priceLabel(strings),
                        Modifier.fontSize(14.px).fontWeight(FontWeight.ExtraBold)
                            .letterSpacing((-0.02).em).whiteSpace(WhiteSpace.NoWrap),
                    )
                }
            }
        }

        Link(
            path = locale.plansPath,
            modifier = Modifier
                .fillMaxWidth()
                .display(DisplayStyle.Flex).alignItems("center")
                .justifyContent("space-between").gap(10.px)
                .padding(topBottom = 13.px, leftRight = 20.px)
                .fontSize(12.px).fontWeight(FontWeight.Bold)
                .letterSpacing(0.08.em).textTransform(TextTransform.Uppercase),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) {
            SpanText(strings.finderAll)
            ArrowRightIcon(18, palette.ink)
        }
    }
}

@Composable
private fun Box(modifier: Modifier, content: @Composable () -> Unit) {
    Div(modifier.toAttrs()) { content() }
}

/** Four rows is what the design's card has height for; asking for more would only be discarded. */
private const val FINDER_RESULTS = 4
